import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function CreatePool() {
  const { t } = useLang();
  const { fmt } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const userMax = user?.tier_max_pool || 20;
  const [form, setForm] = useState({
    name: "", amount_per_round: 1, member_count: 5,
    frequency: "daily", description: ""
  });
  const [loading, setLoading] = useState(false);

  const pot = Number(form.amount_per_round) * Number(form.member_count);
  const overLimit = pot > userMax;

  const submit = async (e) => {
    e.preventDefault();
    if (overLimit) { toast.error(t("lockedTier")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/pools", {
        name: form.name,
        amount_per_round: Number(form.amount_per_round),
        member_count: Number(form.member_count),
        frequency: form.frequency,
        description: form.description,
      });
      toast.success(t("create") + " ✓");
      navigate(`/pools/${data.pool_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-bold text-cyan-gradient mb-2">{t("createPool")}</h1>
      <p className="text-sm text-[#8FA3BD] mb-8">{t("step2")} • {t("maxPool")}: {userMax === 999999 ? "∞" : `$${userMax}`}</p>

      {user?.kyc_status !== "approved" && (
        <div className="p-4 mb-6 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-sm text-[#D4AF37]">
          {t("pendingKyc")}
        </div>
      )}

      <form onSubmit={submit} className="bento-card p-7 space-y-5">
        <Field label={t("poolName")} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-[#050E1A] border-[#1A2D4D] text-white" data-testid="pool-name-input" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("amountPerRound") + " ($)"}>
            <Input type="number" min="1" step="0.5" value={form.amount_per_round} onChange={(e) => setForm({ ...form, amount_per_round: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white" data-testid="pool-amount-input" />
          </Field>
          <Field label={t("memberCount")}>
            <Input type="number" min="2" max="20" value={form.member_count} onChange={(e) => setForm({ ...form, member_count: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white" data-testid="pool-members-input" />
          </Field>
          <Field label={t("frequency")}>
            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
              <SelectTrigger className="bg-[#050E1A] border-[#1A2D4D] text-white" data-testid="pool-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
                <SelectItem value="daily">{t("daily")}</SelectItem>
                <SelectItem value="monthly">{t("monthly")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label={t("description")}>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white" data-testid="pool-desc-input" />
        </Field>
        <div className="p-4 rounded-lg bg-[#050E1A] border border-[#1A2D4D]">
          <div className="text-xs text-[#8FA3BD]">{t("payout")} / {t("round")}:</div>
          <div className={`text-2xl font-bold ${overLimit ? "text-red-400" : "text-cyan-gradient"}`} data-testid="pool-pot-preview">{fmt(pot)}</div>
          {overLimit && <div className="text-xs text-red-400 mt-1">{t("lockedTier")}</div>}
        </div>
        <Button type="submit" disabled={loading || overLimit} className="btn-cyan w-full" data-testid="pool-create-submit-btn">
          {loading ? "..." : t("create")}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[#8FA3BD] text-xs mb-1.5 block">{label}{required && " *"}</Label>
      {children}
    </div>
  );
}
